import type { RiskSignal } from "./risk-signal-card-utils";

const BOOKMARK_QUEUE_KEY = "fintheon:pending-bookmarks:risk-signals";
const ARTIFACT_QUEUE_KEY = "fintheon:risk-signal-artifacts";

interface RiskSignalActionPayload {
  sourceType: "risk_signal";
  sourceId: string;
  title: string;
  summary: string;
  imageUrl?: string | null;
  metadata: Record<string, unknown>;
}

function buildPayload(signal: RiskSignal): RiskSignalActionPayload {
  return {
    sourceType: "risk_signal",
    sourceId: signal.id,
    title: signal.title,
    summary: signal.summary || signal.analysis,
    imageUrl: signal.imageUrl ?? null,
    metadata: {
      score: signal.score,
      severity: signal.severity,
      direction: signal.direction ?? "neutral",
      generatedAt: signal.generatedAt,
      source: signal.source,
      narrativeThreads: signal.narrativeThreads,
      relatedHeadlines: signal.relatedHeadlines,
    },
  };
}

function appendLocalQueue(key: string, payload: RiskSignalActionPayload) {
  try {
    const raw = localStorage.getItem(key);
    const current = raw ? (JSON.parse(raw) as RiskSignalActionPayload[]) : [];
    const next = [
      payload,
      ...current.filter((item) => item.sourceId !== payload.sourceId),
    ].slice(0, 40);
    localStorage.setItem(key, JSON.stringify(next));
  } catch {
    /* local queue is best-effort only */
  }
}

export function askHarperAboutRiskSignal(signal: RiskSignal) {
  const lines = [
    "[Risk Signal Context]",
    `Title: ${signal.title}`,
    `Score: ${signal.score.toFixed(1)} / 10`,
    `Direction: ${signal.direction ?? "neutral"}`,
    `Summary: ${signal.summary || signal.analysis}`,
    signal.imageUrl ? `Image: ${signal.imageUrl}` : null,
    "",
    "Break this risk signal down. Is it going to affect this or any future sessions this week?",
  ].filter((line): line is string => Boolean(line));
  window.dispatchEvent(
    new CustomEvent("fintheon:ask-harper-risk-context", {
      detail: buildPayload(signal),
    }),
  );
  window.dispatchEvent(
    new CustomEvent("fintheon:send-chat-text", {
      detail: { text: lines.join("\n") },
    }),
  );
}

export function queueRiskSignalBookmark(signal: RiskSignal) {
  const payload = buildPayload(signal);
  appendLocalQueue(BOOKMARK_QUEUE_KEY, payload);
  window.dispatchEvent(
    new CustomEvent("fintheon:bookmark-save", { detail: payload }),
  );
}

export function saveRiskSignalArtifact(signal: RiskSignal) {
  const payload = buildPayload(signal);
  appendLocalQueue(ARTIFACT_QUEUE_KEY, payload);
  window.dispatchEvent(
    new CustomEvent("fintheon:risk-signal-artifact", { detail: payload }),
  );
}

export async function shareRiskSignalImageCard(signal: RiskSignal) {
  const payload = buildPayload(signal);
  window.dispatchEvent(
    new CustomEvent("fintheon:risk-signal-image-card", { detail: payload }),
  );
  const text = [
    signal.title,
    signal.summary || signal.analysis,
    `Score ${signal.score.toFixed(1)} / Direction ${signal.direction ?? "neutral"}`,
    signal.imageUrl ?? signal.imageUnavailableReason ?? "",
  ]
    .filter(Boolean)
    .join("\n");
  if (!navigator.clipboard) return;
  await navigator.clipboard.writeText(text);
}
